import useAppContext from '@groupefungo/react-apollo.core/app/UseContext';
import useCommonFragment from '../../../../../base/hooks/CommonFragments';
import useOrderFragment from '../../orders/hooks/useOrderFragment';
import useEventGql from '../../events/hooks/useEventGql';
import useClientGql from '../../clients/hooks/useClientGql';
import useAvailabilityGql from '../../availabilities/hooks/useAvailabilityGql';
import { initialAppointmentForm } from './initialAppointmentForm';
import useNodesGql from '../../../../../base/hooks/useNodesGql';

export default () => {
  const { gql, useQuery, useMutation } = useAppContext();
  const { PageInfoFragment, ConfirmationReducedFragment, ConfirmationFragment, AddressFragment, InspirationFragment } = useCommonFragment();
  const { OrderFragment } = useOrderFragment();
  const { EventsListFragment, EventForCalendarFragment } = useEventGql();
  const { ClientFragment, ClientReducedFragment, ClientNameOnlyFragment } = useClientGql();
  const { UsersAvailabilityFragment } = useAvailabilityGql();

  const ClientsEventReducedFragment = gql`
fragment ClientsEventReducedFragment on ClientsEvent {
  clientAttributes {
    gid
    ...ClientReducedFragment
  }
  event {
    ...EventForCalendarFragment
    address {
      ...AddressFragment
    }
    inspirations {
      ...InspirationFragment
    }
  }
  orderAttributes {
    ...OrderFragment
  }
}  
${ClientReducedFragment}
${OrderFragment}
${EventForCalendarFragment}
${AddressFragment}
${InspirationFragment}
  `;

  const ClientsEventReducedForCalendarFragment = gql`
fragment ClientsEventReducedForCalendarFragment on ClientsEvent {
  clientAttributes {
    gid
    isVip
    ...ClientNameOnlyFragment
  }
}  
${ClientNameOnlyFragment}
  `;

  const ClientsEventFragment = gql`
fragment ClientsEventFragment on ClientsEvent {
  clientAttributes {
    gid
    ...ClientFragment
  }
  event {
    ...EventFragment
    address {
      ...AddressFragment
    }
    inspirations {
      ...InspirationFragment
    }
  }
  orderAttributes {
    ...OrderFragment
  }
}  
${ClientFragment}
${OrderFragment}
${EventsListFragment}
${AddressFragment}
${InspirationFragment}

  `;

  const AppointmentOnlyFragment = gql`
fragment AppointmentOnlyFragment on Appointment {
  id
  gid
  availabilityFrom
  usersAvailabilityId
  clientsEventId
  conciergeOption
  appointmentType
  dressType
  linkToRoomVisible
  createdAt
  status
  statusTimestamp
}
  `;

  const AppointmentCalendarFragment = gql`
fragment AppointmentCalendarFragment on Appointment {
  id
  gid
  usersAvailability {
    id
    needsReassignment
    previousUser {
      id
      fullName
    }
    user {
      id
      fullName
    }
    availability {
      id
      from
      duration
    }
    duration
  }
  confirmation {
    ...ConfirmationReducedFragment
  }
  clientsEventId
  clientsEvent {
    ...ClientsEventReducedForCalendarFragment
  }
  availabilityFrom
  conciergeOption
  appointmentType
  createdAt
  status
}
${ConfirmationReducedFragment}
${ClientsEventReducedForCalendarFragment}
  `;

  const AppointmentListFragment = gql`
fragment AppointmentListFragment on Appointment {
  id
  gid
  usersAvailability {
    id
    needsReassignment
    previousUser {
      id
      fullName
    }
    availability {
      id
      from
      to
      duration
    }
    userId
    user {
      id
      fullName
    }
  }
  clientsEvent {
    event {
      gid
      eventDate
      eventCode
      inspirations {
        notes
      }
    }
    clientAttributes {
      gid
      fullName
      email
      phoneNumbers {
        phoneNo
        isBest
      }
    }
    role {
      name
    }
  }
  conciergeOption
  appointmentType
  dressType
  createdAt
  status
  statusTimestamp
}
`;

  const AppointmentFragment = gql`
fragment AppointmentFragment on Appointment {
  id
  gid
  usersAvailabilityId
  usersAvailability {
    id
    needsReassignment
    previousUser {
      id
      fullName
    }
    conciergeOption
    availability {
      id
      from
      to
      duration
    }
    userId
    user {
      id
      firstName
      lastName
      fullName
    }
    duration
  }
  confirmation {
    ...ConfirmationFragment
  }
  clientsEventId
  clientsEvent {
    ...ClientsEventReducedFragment
    role {
      name
    }
  }
  room {
    id
    referenceNumber
    status
  }
  availabilityFrom
  conciergeOption
  appointmentType
  dressType
  linkToRoomVisible
  createdAt
  status
  statusTimestamp
  sale
  followUpCategories
}
${ConfirmationFragment}
${ClientsEventReducedFragment}
  `;

  const onTypeAppointment = gql`
fragment OnAppointmentFragment on NodeInterface {
  ... on Appointment {
    ...AppointmentFragment
  }
}
${AppointmentFragment}
  `;

  const AppointmentEdgeFragment = gql`
fragment AppointmentEdgeFragment on AppointmentEdge {
  node {
    ... on Appointment {
      ...AppointmentListFragment
    }
  }
}
${AppointmentListFragment}
  `;
  const AppointmentConnectionFragment = gql`
fragment AppointmentConnectionFragment on AppointmentConnection {
  edges {
    ...AppointmentEdgeFragment
  }
  pageInfo {
    ...PageInfoFragment
  }
}
${AppointmentEdgeFragment}  
${PageInfoFragment}  
  `;

  const AppointmentsGql = gql`
  query appointments($filters: FiltersInput) {
  appointments(filters: $filters) {
    ...AppointmentFragment
  }
}  
${AppointmentFragment}
  `;

  const AppointmentsCalendarGql = gql`
  query appointments($filters: FiltersInput) {
  appointments(filters: $filters) {
    ...AppointmentCalendarFragment
  }
}  
${AppointmentCalendarFragment}
  `;

  const AppointmentConnectionGql = gql`
  query($first: Int, $last: Int, $after: String, $before: String, $filters: FiltersInput) {
  appointmentsConnection(first: $first, last: $last, after: $after, before: $before, filters: $filters) {
    ...AppointmentConnectionFragment
  }
}
${AppointmentConnectionFragment}
  `;

  const AppointmentConnectionSearchGql = gql`
  query($first: Int, $last: Int, $after: String, $before: String, $filters: FiltersInput) {
  appointmentsConnection(first: $first, last: $last, after: $after, before: $before, filters: $filters) {
    edges {
      node {
        id
        email
        firstName
        lastName
      }
    }
  }
}
  `;

  const MutateAppointmentGql = gql`
mutation mutateAppointment($input: AppointmentInput!) {
  mutateAppointment(input: $input) {
    appointment {
      id
      gid
      usersAvailabilityId
      clientsEventId
      appointmentType
      conciergeOption
      dressType
    }
  }
}   
  `;

  const AppointmentNodeGql = gql`
  query appointmentNode($gid: ID!) {
    node(gid: $gid) {
      id
      gid
      ...AppointmentFragment
    }
  }
  ${AppointmentFragment}
`;

  const ReassignGql = gql`
    mutation reassign($input: ReassignInput!) {
      reassign(input: $input) {
        usersAvailability {
          ...UsersAvailabilityFragment
        }
        appointment {
          ...AppointmentFragment
        }
      }
    }
${UsersAvailabilityFragment}
${AppointmentFragment}
`;

  const ChangeDurationGql = gql`
    mutation changeDuration($input: ChangeDurationInput!) {
      changeDuration(input: $input) {
        usersAvailability {
          ...UsersAvailabilityFragment
        }
        appointment {
          ...AppointmentFragment
        }
      }
    }
${UsersAvailabilityFragment}
${AppointmentFragment}
`;

  const CancelAppointmentGql = gql`
mutation cancelAppointment($id: ID!) {
  cancelAppointment(id: $id) {
    appointment {
      ...AppointmentFragment
    }
  }
}
${AppointmentFragment}
`;

  const ConfirmAppointmentGql = gql`
      mutation confirmAppointment($id: ID!) {
        confirmAppointment(id: $id) {
          appointment {
            ...AppointmentFragment
          }
        }
      }
${AppointmentFragment}
  `;

  const FreeIncompleteReservationsGql = gql`
    mutation{
      freeIncompleteReservations{
        status
      }
    }
  `;

  const CompleteAppointmentGql = gql`
      mutation completeAppointment($id: ID!, $resultedInSale: Boolean!, $followUpCategories: [String!]) {
        completeAppointment(id: $id, resultedInSale: $resultedInSale, followUpCategories: $followUpCategories) {
          appointment {
            ...AppointmentFragment
          }
          klaviyoStatus
        }
      }
${AppointmentFragment}
  `;

  const NoShowAppointmentGql = gql`
      mutation noShowAppointment($id: ID!) {
        noShowAppointment(id: $id) {
          appointment {
            ...AppointmentFragment
          }
        }
      }
${AppointmentFragment}
  `;

  const SaleFromAppointmentGql = gql`
      mutation saleFromAppointment($id: ID!) {
        saleFromAppointment(id: $id) {
          appointment {
            ...AppointmentFragment
          }
          klaviyoStatus
        }
      }
${AppointmentFragment}
  `;

  const UpdateFollowUpCategoriesGql = gql`
      mutation updateFollowUpCategories($id: ID!, $followUpCategories: [String!]!) {
        updateFollowUpCategories(id: $id, followUpCategories: $followUpCategories) {
          appointment {
            ...AppointmentFragment
          }
        }
      }
${AppointmentFragment}
  `;

  const useFreeIncompleteReservationsMutation = () => useMutation(FreeIncompleteReservationsGql);

  const ResendConfirmationEmail = gql`
      mutation resendConfirmationEmail($id: ID!) {
        resendConfirmationEmail(id: $id){
          text
        }
      }
  `;

  const useAppointmentConnectionQuery = (connection) => {
    const { first, last, before, after, ...filters } = connection;
    const variables = { first, last, before, after, filters };

    return useQuery(AppointmentConnectionGql, {
      variables,
      fetchPolicy: 'network-only',
    });
  };

  const useAppointmentCalendarFragmentQuery = (filters, admin) => useQuery(AppointmentsCalendarGql, {
    variables: { filters },
    skip: !admin,
  });

  const useAppointmentConnectionSearchQuery = (connection) => {
    const { first, last, before, after, ...filters } = connection;
    const variables = { first, last, before, after, filters };

    return useQuery(AppointmentConnectionSearchGql, {
      variables,
      fetchPolicy: 'network-only',
    });
  };

  const useMutateAppointment = (input) => useMutation(MutateAppointmentGql, {
    variables: { input },
  });

  const useReassignMutation = (input) => useMutation(ReassignGql, {
    variables: { input },
  });

  const useChangeDurationMutation = (input) => useMutation(ChangeDurationGql, {
    variables: { input },
  });

  const useCancelAppointment = (id) => useMutation(CancelAppointmentGql, {
    variables: { id },
  });

  const useConfirmAppointment = (id) => useMutation(ConfirmAppointmentGql, {
    variables: { id },
  });

  const useAppointmentNodeQuery = (gid) => useQuery(AppointmentNodeGql, {
    variables: { gid },
    fetchPolicy: 'network-only',
    skip: !gid,
  });

  const useMutateAppointmentMutation = (input) => useMutateAppointment(input);

  const useAppointmentQuery = (isNewAppointment, gid) => (isNewAppointment ? {
    data: { appointment: { ...initialAppointmentForm } },
    loading: false,
  } : useAppointmentNodeQuery(gid));

  const { useNodesQuery } = useNodesGql('OnAppointmentFragment', onTypeAppointment);

  const useAppointmentNodesGql = (gids) => useNodesQuery(gids);

  const useResendConfirmationEmail = (id) => useMutation(ResendConfirmationEmail, {
    variables: { id },
  });

  const useCompleteAppointment = (id) => useMutation(CompleteAppointmentGql, {
    variables: { id },
  });

  const useCompleteAppointmentWithSale = (id) => useMutation(CompleteAppointmentGql, {
    variables: { id, resultedInSale: true },
  });

  const useCompleteAppointmentWithoutSale = (id) => useMutation(CompleteAppointmentGql, {
    variables: { id, resultedInSale: false },
  });

  const useNoShowAppointment = (id) => useMutation(NoShowAppointmentGql, {
    variables: { id },
  });

  const useSaleFromAppointment = (id) => useMutation(SaleFromAppointmentGql, {
    variables: { id },
  });

  const useUpdateFollowUpCategories = (id) => useMutation(UpdateFollowUpCategoriesGql, {
    variables: { id },
  });

  return {
    AppointmentFragment,
    AppointmentCalendarFragment,
    AppointmentOnlyFragment,
    useAppointmentCalendarFragmentQuery,
    useAppointmentConnectionQuery,
    useAppointmentConnectionSearchQuery,
    useAppointmentNodeQuery,
    useMutateAppointmentMutation,
    useAppointmentQuery,
    useReassignMutation,
    useAppointmentNodesGql,
    useCancelAppointment,
    useConfirmAppointment,
    useFreeIncompleteReservationsMutation,
    useResendConfirmationEmail,
    useCompleteAppointment,
    useCompleteAppointmentWithSale,
    useCompleteAppointmentWithoutSale,
    useNoShowAppointment,
    useSaleFromAppointment,
    useUpdateFollowUpCategories,
    useChangeDurationMutation,
  };
};
